import React from 'react';
import { Info } from 'lucide-react';
import { StepNavButtons } from './StepNavButtons';
import { MarkdownContent } from '../MarkdownContent';
import { parseVideoEmbed } from '@/lib/utils/videoEmbed';
import type { ModuleStep } from '@/lib/models/module';

interface InfoStepRendererProps {
    step: ModuleStep;
    onSubmit: () => void;
    isSubmitting: boolean;
    canGoBack: boolean;
    onGoBack: () => void;
}

export const InfoStepRenderer: React.FC<InfoStepRendererProps> = ({ step, onSubmit, isSubmitting, canGoBack, onGoBack }) => {
    const { heading, body, image_url, video_url } = step.content;
    const video = video_url ? parseVideoEmbed(video_url) : null;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Info className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900">{heading || step.step_title}</h2>
                </div>
            </div>

            {/* Image */}
            {image_url && (
                <img src={image_url} alt={heading} className="w-full rounded-xl object-cover max-h-64" />
            )}

            {/* Video — YouTube/Vimeo iframe or direct <video> element */}
            {video && video.src && (
                <div className="w-full aspect-video rounded-xl overflow-hidden bg-black">
                    {video.kind === 'iframe' ? (
                        <iframe
                            src={video.src}
                            title={heading || step.step_title}
                            className="w-full h-full"
                            frameBorder={0}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <video src={video.src} controls className="w-full h-full" />
                    )}
                </div>
            )}

            {/* Body */}
            {body && <MarkdownContent>{body}</MarkdownContent>}

            {/* #295 — sub_points were rendered in the employer's step preview
                (ModuleStepContent) and nowhere in the player, so an authored
                bullet list existed in the data, showed in preview, and the
                candidate never saw it. */}
            {Array.isArray(step.content?.sub_points) && step.content.sub_points.length > 0 && (
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {step.content.sub_points.map((p: string, i: number) => <li key={i}>{p}</li>)}
                </ul>
            )}

            {/* Navigation */}
            <StepNavButtons
                canGoBack={canGoBack}
                onGoBack={onGoBack}
                onNext={onSubmit}
                nextLabel={isSubmitting ? 'Laddar...' : 'Fortsätt'}
                nextDisabled={isSubmitting}
            />
        </div>
    );
};
